/**
=========================================================
* Iris Design Limited  Fulfilment POD - v2.2.0
=========================================================

* Product Page: https://www.irisdesignlimited.com/product/material-dashboard-react
* Copyright 2023 Dev Iris Design Limited Company (https://www.irisdesignlimited.com)

Coded by www.irisdesignlimited.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import CachedIcon from '@mui/icons-material/Cached';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useEffect, useMemo, useState } from "react";
// react-router components
import { Link, useLocation, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";
// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Toolbar from "@mui/material/Toolbar";
import * as XLSX from 'xlsx';

// Iris Design Limited  Fulfilment POD components
import MDBox from "components/MDBox";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
// Iris Design Limited  Fulfilment POD example components
import { styled } from '@mui/material/styles';
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";
// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarIconButton,
  navbarMobileMenu,
  navbarRow,
} from "examples/Navbars/DashboardNavbar/styles";

// Iris Design Limited  Fulfilment POD context
import { Button, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import bcrypt from 'bcryptjs';
import MDTypography from "components/MDTypography";
import {
  setMiniSidenav,
  setOpenConfigurator,
  setTransparentNavbar,
  useMaterialUIController,
} from "context";
import { exportOrder, getDashboard, getDesign, getNewOrder, getOrders, getOrdersSearch, getStore, getUser, logout, reloadStore, updateConfirmCallFilter, updateCurrentPage, updateEndDate, updateOrderStatus, updateSearch, updateStartDate, updateStore, updateType } from "features/slices";
import moment from "moment";
import { NotificationManager } from "react-notifications";
import { useDispatch, useSelector } from "react-redux";
import { mergeStoreFilterOptions } from "constants/storeFilters";
const CustomTextField = styled(TextField)(({ theme, search }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: '0.8125rem',
    backgroundColor: '#ffffff',
    minHeight: 40,
    '& fieldset': {
      borderColor: search ? theme.palette.primary.main : '#e2e8f0',
    },
    '&:hover fieldset': {
      borderColor: search ? theme.palette.primary.dark : '#cbd5e1',
    },
    '&.Mui-focused fieldset': {
      borderColor: search ? theme.palette.primary.main : '#64748b',
      borderWidth: 1,
    },
  },
  '& .MuiInputBase-input': {
    py: '10px',
    fontSize: '0.8125rem',
  },
}));

/** Style chung cho Select filter trên navbar (đồng bộ viền / chiều cao / bo góc). */
const navSelectSx = {
  height: 40,
  borderRadius: '8px',
  fontSize: '0.8125rem',
  fontWeight: 500,
  bgcolor: '#ffffff',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e2e8f0',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#cbd5e1',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#64748b',
    borderWidth: 1,
  },
  '& .MuiSelect-select': {
    py: '10px',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiSelect-icon': {
    color: '#64748b',
  },
};

const navActionButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.8125rem',
  px: 2,
  minHeight: 40,
  borderRadius: '8px',
  borderColor: '#e2e8f0',
  color: '#334155',
  bgcolor: '#ffffff',
  '&:hover': {
    borderColor: '#cbd5e1',
    bgcolor: '#f8fafc',
  },
};

const FULFIL_STATUS_OPTIONS = [
  { value: "", name: "None" },
  { value: "not_fulfilled", name: "Not Fulfilled" },
  { value: "fulfilled", name: "Fulfilled" },
  { value: "refund", name: "Refund" },
  { value: "tracking", name: "No Tracking" },
  { value: "basecost", name: "No Basecost" },
  { value: "deli", name: "Undelivered" },
  { value: "case_item", name: "Case item not received" },
  { value: "case_des", name: "Case not as described" },
];

const CONFIRM_CALL_OPTIONS = [
  { value: "", name: "None" },
  { value: "confirm_call", name: "Confirm Call" },
  { value: "called", name: "Called" },
  { value: "not_yet_called", name: "Not yet called" },
];

/** Luôn hiển thị label đúng khi value "" (None); tránh ô Select trống với MUI Outlined. */
function renderNavSelectValue(selected, options) {
  const v = selected ?? "";
  const opt = options.find((o) => o.value === v);
  return opt ? opt.name : "None";
}

function DashboardNavbar({ absolute, light, isMini }) {
  const dispatch2 = useDispatch();
  const user = useSelector((state) => state.user.user);
  const permission = useSelector((state) => state.user.permission || []);
  const startDate = useSelector((state) => state.user.startDate);
  const endDate = useSelector((state) => state.user.endDate);
  const search = useSelector((state) => state.user.search);
  const currentPage = useSelector((state) => state.user.currentPage) || 1;
  const order_status = useSelector((state) => state.user.order_status ?? "");
  const filterStore = useSelector((state) => state.user.filterStore);
  const type = useSelector((state) => state.user.type);
  const confirmCallFilter = useSelector((state) => state.user.confirmCallFilter ?? "");
  const store = useSelector((state) => state.user.store);
  const storeFilterOptions = useMemo(
    () => mergeStoreFilterOptions(store || []),
    [store]
  );
  const [navbarType, setNavbarType] = useState("relative");
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const handleChangeOrderStatus = async (event) => {
    await dispatch2(updateOrderStatus(event.target.value))

  };
  const handleChangeConfirmCallFilter = async (event) => {
    const value = event.target.value;
    await dispatch2(updateConfirmCallFilter(value));
    await dispatch2(updateCurrentPage(1));
  };
  const handleGetOrder = async () => {
    await dispatch2(getNewOrder());

    bcrypt.genSalt(10, (err, salt) => {
      bcrypt.hash(`${user}đố tuấn anh phá được`, salt, async (err, hash) => {
        const time = moment().unix()
        const res = await dispatch2(getOrders({
          store: filterStore, order_status,
          startDate: moment(startDate).startOf("day").format("x"),
          endDate: moment(endDate).endOf("day").format("x"),
          currentPage,
          search,
          version: hash,
          type
        }))

      });
    })
    bcrypt.genSalt(10, (err, salt) => {
      bcrypt.hash(`${user}đố tuấn anh phá được`, salt, async (err, hash) => {
        const time = moment().unix()
        const res = await dispatch2(getDashboard({
          startDate: moment(startDate).startOf("day").format("x"),
          endDate: moment(endDate).endOf("day").format("x"), store: filterStore, order_status, version: hash,
        }))
      });
    })

  }
  function modifyUrlString(inputString) {
    if (inputString.includes("/cdn-cgi")) {
      const regex = /https:\/\//g;
      const matches = inputString.match(regex);
      if (!matches) {
        return inputString;
      } else if (matches.length === 1) {
        return 'https://' + inputString.split('https://')[1]
      } else if (matches.length === 2) {
        const parts = inputString.split('https://');
        return 'https://' + parts[2];
      } else {
        return inputString;
      }
    } else {
      return inputString;
    }
  }

  const extractJerseyName = (text) => {
    // Ưu tiên tìm Name
    const regexCustomizeName = /Customize Jersey Name:\s*(.*)/;
    const regexCustomName = /Custom Jersey Name:\s*(.*)/;

    const matchCustomizeName = text.match(regexCustomizeName);
    if (matchCustomizeName) {
      return `Customize Jersey Name: ${matchCustomizeName[1].trim()}`;
    }

    const matchCustomName = text.match(regexCustomName);
    if (matchCustomName) {
      return `Customize Jersey Name: ${matchCustomName[1].trim()}`;
    }

    // Nếu không có Name, tìm Number
    const regexCustomizeNumber = /Customize Jersey Number:\s*(.*)/;
    const regexCustomNumber = /Custom Jersey Number:\s*(.*)/;

    const matchCustomizeNumber = text.match(regexCustomizeNumber);
    if (matchCustomizeNumber) {
      return `Customize Jersey Number: ${matchCustomizeNumber[1].trim()}`;
    }

    const matchCustomNumber = text.match(regexCustomNumber);
    if (matchCustomNumber) {
      return `Customize Jersey Number: ${matchCustomNumber[1].trim()}`;
    }

    // Ưu tiên tìm Name
    const regexCustomizeName2 = /Customize Name:\s*(.*)/;
    const regexCustomName2 = /Customize Number:\s*(.*)/;

    const matchCustomizeName2 = text.match(regexCustomizeName2);
    if (matchCustomizeName2) {
      return `Customize Jersey Name: ${matchCustomizeName2[1].trim()}`;
    }

    const matchCustomName2 = text.match(regexCustomName2);
    if (matchCustomName2) {
      return `Customize Jersey Name: ${matchCustomName2[1].trim()}`;
    }

    return "";
  };


  const extractBeforeCustomizeJersey = (text) => {
    const match = text.match(/(Customize:|Customize Name\/)[^-]*/);
    if (match || text.includes("Jersey")) {
      const index = text.indexOf('- Custom');
      if (index !== -1) {
        return text.substring(0, index).trim();
      }
      return text;
    }
    return text;
  }
  const extractFromCustom = (text) => {
    const match = text.match(/(Customize:|Customize Name\/)[^-]*/);
    return match ? match[0].trim() : "";
  }


  const handleExport = async () => {
    try {
      const newData = []
      bcrypt.genSalt(10, (err, salt) => {
        bcrypt.hash(`${user}đố tuấn anh phá được`, salt, async (err, hash) => {
          const time = moment().unix()
          const res = await dispatch2(exportOrder({
            store: filterStore, order_status,
            startDate: moment(startDate).startOf("day").format("x"),
            endDate: moment(endDate).endOf("day").format("x"),
            search,
            version: hash,
          }))
          newData.push(["Date", "Order ID", "Store Name", "Shield", "Basecost", "Price", "LineShip", "Tracking", "Items Name", "Size", "Custom", "Custom Name", "Color", "SKU", "Fulfil", "Link", "Link Image", "Quantity", "Name Customer", "Address 1", "Address 2", "City", "State", "Zip Code", "Address", "Status Fulfil", "Phone", "Note", "Shipping Method", "Email", "Front Design", "Back Design", "Send message customer", "Deli", "Nhung Check", "Mai Check", "Status"])
          if (res.payload.data) {
            for (let _data of res.payload.data) {
              newData.push([moment(_data.date).format("DD/MM/YYYY"), _data.order_id, _data.store_name, _data.shield, _data.basecost, _data.price, _data.lineship, _data.tracking, _data.items_name, extractBeforeCustomizeJersey(_data.size), extractFromCustom(_data.size), `${extractJerseyName(_data.size)}`, _data.color, !_data.size.includes("Jersey Type") && _data.store_name == "zonemarts.com" ? `3D` : _data.sku, _data.fulfil, _data.link, modifyUrlString(_data.images), _data.quantity, _data.customer_name, _data.address?.[0], _data.address?.[1], _data.address?.[2], _data.address?.[3], _data.address?.[4], `${_data.address?.[0]} ${_data.address?.[1]}, ${_data.address?.[2]}, ${_data.address?.[3]}, ${_data.address?.[4]}, United States`, _data.status_fulfil, _data.phone, _data.note, _data.shipping_method, _data.email, _data?.design_link?.front, _data?.design_link?.back, _data.is_message_customer, _data.deli, _data.nhung, _data.mai, _data.error_order])
            }
            const ws = XLSX.utils.aoa_to_sheet(newData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
            XLSX.writeFile(wb, `Export Order ${moment(startDate).format("DD/MM")} - ${moment(endDate).format("DD/MM")}.csv`);
          }
        });
      })

    } catch (error) {
      NotificationManager.error(error.message, 'Error', 1000);
    }

  }
  const handleChange = async (event) => {
    await dispatch2(updateStore(event.target.value))
    bcrypt.genSalt(10, (err, salt) => {
      bcrypt.hash(`${user}đố tuấn anh phá được`, salt, async (err, hash) => {
        const time = moment().unix()
        const res = await dispatch2(getOrders({
          store: filterStore, order_status,
          startDate: moment(startDate).startOf("day").format("x"),
          endDate: moment(endDate).endOf("day").format("x"),
          currentPage,
          search,
          version: hash,
          type
        }))

      });
    })
  };
  const route = useLocation().pathname.split("/").slice(1);

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleCloseMenu = () => setOpenMenu(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleChangeDate = (type, value) => {
    if (type == "startDate") {
      dispatch2(updateStartDate(value));
    } else {
      dispatch2(updateEndDate(value));
    }
  };
  const handleToggle = async (event) => {
    dispatch2(updateType(event.target.checked))
  };
  const handleSetSearch = (value) => {
    dispatch2(updateSearch(value));
  };
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (user != "htntmmiua@gmail.com" && user != "duongtrieuvy33@gmail.com") {
        if (location.pathname == "/library") {
          dispatch2(getDesign({
            search
          }));
        } else {
          // dispatch2(getOrders({
          //   store: filterStore, order_status,
          //   startDate: moment(startDate).startOf("day").format("x"),
          //   endDate: moment(endDate).endOf("day").format("x"), currentPage: 1, search, type
          // }));
          bcrypt.genSalt(10, (err, salt) => {
            bcrypt.hash(`${user}đố tuấn anh phá được`, salt, async (err, hash) => {
              const time = moment().unix()
              const res = await dispatch2(getOrders({
                store: filterStore, order_status,
                startDate: moment(startDate).startOf("day").format("x"),
                endDate: moment(endDate).endOf("day").format("x"),
                currentPage,
                search,
                version: hash,
                type
              }))

            });
          })
        }
      } else {
        dispatch2(getOrdersSearch({
          store: filterStore, order_status,
          startDate: moment(startDate).startOf("day").format("x"),
          endDate: moment(endDate).endOf("day").format("x"), currentPage: 1, search
        }));
      }
    }
  };

  useEffect(() => {
    // if (user && user != "tuanah252@gmail.com") {
    if (user) {
      const fetchOrders = async () => {
        if (user != "htntmmiua@gmail.com" && user != "duongtrieuvy33@gmail.com") {
          if (location.pathname !== "/error" && location.pathname !== "/dashboard" && location.pathname !== "/products" && location.pathname !== "/feed" && location.pathname !== "/store" && location.pathname !== "/library") {
            bcrypt.genSalt(10, (err, salt) => {
              bcrypt.hash(`${user}đố tuấn anh phá được`, salt, async (err, hash) => {
                const time = moment().unix()
                const res = await dispatch2(getOrders({
                  store: filterStore, order_status,
                  startDate: moment(startDate).startOf("day").format("x"),
                  endDate: moment(endDate).endOf("day").format("x"),
                  currentPage,
                  search,
                  version: hash,
                  type
                }))

              });
            })
            user == "luoiquantam2607@gmail.com" ? await dispatch2(
              getUser()
            ) : null

          } else if (location.pathname == "/dashboard") {
            // Dashboard component tự xử lý API call, không cần gọi ở đây
            // Chỉ cần reload store nếu cần
            user == "tuanah252@gmail.com" || !permission || permission.length == 0 ? await dispatch2(reloadStore()) : await dispatch2(
              getStore()
            )
          } else if (location.pathname == "/store") {
            // Store page vẫn cần getDashboard để cập nhật permission
            bcrypt.genSalt(10, (err, salt) => {
              bcrypt.hash(`${user}đố tuấn anh phá được`, salt, async (err, hash) => {
                const time = moment().unix()
                const res = await dispatch2(getDashboard({
                  startDate: moment(startDate).startOf("day").format("x"),
                  endDate: moment(endDate).endOf("day").format("x"), store: filterStore, order_status, version: hash, type
                }))
              });
            })
            user == "tuanah252@gmail.com" || !permission || permission.length == 0 ? await dispatch2(reloadStore()) : await dispatch2(
              getStore()
            )
          }
        }
      };
      fetchOrders();
    } else {
      dispatch2(logout())
      navigate('/authentication/log-out');
    }
  }, [startDate, endDate, currentPage, filterStore, order_status, type, confirmCallFilter]);

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem icon={<Icon>email</Icon>} title="Check new messages" />
      <NotificationItem icon={<Icon>podcasts</Icon>} title="Manage Podcast sessions" />
      <NotificationItem icon={<Icon>shopping_cart</Icon>} title="Payment successfully completed" />
    </Menu>
  );

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar
        sx={(theme) => ({
          ...navbarContainer(theme),
          flexWrap: 'wrap',
          gap: { xs: 1.5, md: 2 },
          alignItems: 'center',
          rowGap: 1.5,
        })}
      >
        <MDBox
          sx={(theme) => navbarRow(theme, { isMini })}
          mb={{ xs: 1, md: 0 }}
        >
          <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
            <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
          </MDBox>

          <div className="form-group">
            <MDTypography variant="h6" style={{ marginRight: 5 }}>From </MDTypography>
            <DatePicker
              placeholderText="DD/MM/YYYY"
              dateFormat="dd/MM/yyyy"
              id="start-date"
              autoComplete="off"
              selected={startDate}
              className="custom-datepicker"
              onChange={(e) => handleChangeDate("startDate", e)}
            />
          </div>
          <div className="form-group">
            <MDTypography variant="h6" style={{ marginRight: 5, marginLeft: 5 }}>To </MDTypography>
            <DatePicker
              placeholderText="DD/MM/YYYY"
              dateFormat="dd/MM/yyyy"
              id="start-date"
              autoComplete="off"
              selected={endDate}
              className="custom-datepicker"
              onChange={(e) => handleChangeDate("endDate", e)}
            />
          </div>

        </MDBox>
        <MDBox
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            alignItems: { xs: 'stretch', lg: 'center' },
            gap: 1.5,
            flex: 1,
            minWidth: 0,
            width: { xs: '100%', md: 'auto' },
          }}
        >
          <MDBox
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1.5,
              flex: 1,
              minWidth: 0,
            }}
          >
            <FormControl size="small" sx={{ flex: '1 1 140px', minWidth: { xs: '100%', sm: 140 }, maxWidth: { sm: 240 } }}>
              <InputLabel id="nav-filter-store">Store</InputLabel>
              <Select
                labelId="nav-filter-store"
                value={filterStore}
                onChange={handleChange}
                label="Store"
                sx={navSelectSx}
              >
                <MenuItem value="All Store">All Store</MenuItem>
                {storeFilterOptions.map((storeName) => (
                  <MenuItem key={storeName} value={storeName}>
                    {storeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: '1 1 130px', minWidth: { xs: '100%', sm: 130 }, maxWidth: { sm: 200 } }}>
              <InputLabel id="nav-filter-fulfil" shrink>
                Fulfil Status
              </InputLabel>
              <Select
                labelId="nav-filter-fulfil"
                label="Fulfil Status"
                value={order_status}
                displayEmpty
                renderValue={(v) => renderNavSelectValue(v, FULFIL_STATUS_OPTIONS)}
                notched
                sx={navSelectSx}
                onChange={(e) => handleChangeOrderStatus(e)}
              >
                {FULFIL_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value === "" ? "fulfil-none" : option.value} value={option.value} sx={{ fontSize: '0.8125rem' }}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {(location.pathname === "/services" || location.pathname === "/error") ? (
              <FormControl size="small" sx={{ flex: '1 1 120px', minWidth: { xs: '100%', sm: 120 }, maxWidth: { sm: 190 } }}>
                <InputLabel id="confirm-call-filter-label" shrink>
                  Confirm Call
                </InputLabel>
                <Select
                  labelId="confirm-call-filter-label"
                  label="Confirm Call"
                  value={confirmCallFilter}
                  displayEmpty
                  renderValue={(v) => renderNavSelectValue(v, CONFIRM_CALL_OPTIONS)}
                  notched
                  sx={navSelectSx}
                  onChange={handleChangeConfirmCallFilter}
                >
                  {CONFIRM_CALL_OPTIONS.map((option) => (
                    <MenuItem key={option.value === "" ? "call-none" : option.value} value={option.value} sx={{ fontSize: '0.8125rem' }}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            <CustomTextField
              className="inputText"
              placeholder="Search…"
              value={search}
              onChange={(e) => handleSetSearch(e.target.value)}
              onKeyPress={handleKeyPress}
              search={search}
              size="small"
              sx={{
                flex: '2 1 180px',
                minWidth: { xs: '100%', sm: 160 },
                maxWidth: { lg: 360 },
              }}
            />
          </MDBox>
          {user != "htntmmiua@gmail.com" && user != "duongtrieuvy33@gmail.com" && user != "maitrieu9820@gmail.com" ? (
            <MDBox sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={handleExport}
                startIcon={<FileDownloadIcon sx={{ fontSize: 18, color: 'primary.main' }} />}
                sx={navActionButtonSx}
              >
                Export
              </Button>
              <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={handleGetOrder}
                startIcon={<CachedIcon sx={{ fontSize: 18, color: 'primary.main' }} />}
                sx={navActionButtonSx}
              >
                Order
              </Button>
            </MDBox>
          ) : null}
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox color={light ? "white" : "inherit"}>
              <Link to="/authentication/log-out">
                <IconButton sx={navbarIconButton} size="small" disableRipple>
                  <Icon sx={iconsStyle}>login</Icon>
                </IconButton>
              </Link>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={handleConfiguratorOpen}
              >
                <Icon sx={iconsStyle}>settings</Icon>
              </IconButton>
              {renderMenu()}
            </MDBox>
          </MDBox>
        )}

      </Toolbar>
    </AppBar >
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
